import { inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { WebTracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { Span, trace, SpanStatusCode } from '@opentelemetry/api';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import { environment } from '../../../environments/environment';
import { getApiUrl } from '../config/config-helpers';
import { RuntimeConfigService } from './runtime-config.service';

@Injectable({
  providedIn: 'root'
})
export class TelemetryService {
  private provider?: WebTracerProvider;
  private tracer: any;
  private isInitialized = false;
  private readonly httpBackend = inject(HttpBackend, { optional: true });
  private readonly http = this.httpBackend ? new HttpClient(this.httpBackend) : null;
  private readonly runtimeConfig = inject(RuntimeConfigService);

  constructor() {}

  /**
   * Get the API origin pattern for trace header propagation.
   * Only propagates trace headers to the intended backend, preventing header leakage to third parties.
   * @param endpoint - The OTLP endpoint URL (relative or absolute)
   * @returns RegExp that matches only the intended origin followed by / or end-of-string
   */
  private getApiOriginPattern(endpoint: string): RegExp {
    // Helper to escape special regex characters
    const escapeRegex = (str: string): string => {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    // Defensive: handle null/undefined/empty/whitespace
    if (!endpoint || typeof endpoint !== 'string' || endpoint.trim().length === 0) {
      console.warn('Invalid OTLP endpoint, falling back to current origin for trace header propagation');
      return new RegExp(`^${escapeRegex(window.location.origin)}(?:\\/|$)`);
    }

    // Check for relative path (single slash, not protocol-relative //)
    if (endpoint.startsWith('/') && !endpoint.startsWith('//')) {
      // Relative path - match current origin only, anchored with / or end-of-string
      return new RegExp(`^${escapeRegex(window.location.origin)}(?:\\/|$)`);
    }

    // Absolute URL (including protocol-relative //) - extract and match the origin
    try {
      // Use current location as base for protocol-relative URLs
      const url = new URL(endpoint, window.location.href);
      
      // Validate protocol - only HTTP/HTTPS are valid for OTLP endpoints
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        console.warn(`Invalid OTLP endpoint protocol: ${url.protocol}, falling back to current origin for trace header propagation`);
        return new RegExp(`^${escapeRegex(window.location.origin)}(?:\\/|$)`);
      }
      
      // Anchor with (?:\/|$) to prevent subdomain attacks (e.g., myapp.com.attacker.com)
      return new RegExp(`^${escapeRegex(url.origin)}(?:\\/|$)`);
    } catch (error) {
      // Fallback to current origin if URL parsing fails
      console.warn('Failed to parse OTLP endpoint URL, falling back to current origin for trace header propagation:', error);
      return new RegExp(`^${escapeRegex(window.location.origin)}(?:\\/|$)`);
    }
  }

  /**
   * Initialize OpenTelemetry tracing for the browser
   * @param otlpEndpoint - The endpoint to send traces to (defaults to WebAPI proxy)
   */
  initialize(otlpEndpoint?: string): void {
    if (this.isInitialized) {
      console.warn('TelemetryService already initialized');
      return;
    }

    try {
      // Get endpoint from parameter, window object, or default
      const endpoint = otlpEndpoint 
        || (window as any).__OTEL_ENDPOINT__ 
        || '/api/telemetry/traces';

      console.log('Initializing OpenTelemetry with endpoint:', endpoint);

      // Configure the OTLP exporter to send to WebAPI proxy
      const exporter = new OTLPTraceExporter({
        url: endpoint,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Create span processor
      const processor = new BatchSpanProcessor(exporter);

      // Identify the emitting service on every span. `deployment.environment`
      // reads the environment's own name rather than deriving one from the
      // `production` boolean, which had no value able to mean "test" and so
      // filed every Test span under "development" (issue #766, F6).
      //
      // No `service.version`: nothing in the build carries a real one (the
      // frontend package.json sits at 0.0.0), so any literal here would make
      // every deployment look like the same version in App Insights. An
      // absent attribute is queryable as absent; a wrong one is not.
      const resource = resourceFromAttributes({
        [ATTR_SERVICE_NAME]: 'flopstr-frontend',
        'deployment.environment': environment.deploymentEnvironment,
      });

      // Create the provider with the processor in the config
      this.provider = new WebTracerProvider({
        resource,
        spanProcessors: [processor],
      });

      // Register the provider
      this.provider.register({
        contextManager: new ZoneContextManager(),
      });

      // Register instrumentations
      registerInstrumentations({
        tracerProvider: this.provider,
        instrumentations: [
          // Document load instrumentation for page load performance
          new DocumentLoadInstrumentation(),
          
          // Fetch instrumentation for HTTP requests
          // Only propagate trace headers to the API backend, not third-party services
          new FetchInstrumentation({
            propagateTraceHeaderCorsUrls: [
              this.getApiOriginPattern(endpoint),
            ],
            clearTimingResources: true,
          }),
          
          // User interaction instrumentation for clicks, form submissions, etc.
          new UserInteractionInstrumentation({
            eventNames: ['click', 'submit'],
          }),
        ],
      });

      // Get the tracer instance
      this.tracer = trace.getTracer('frontend', '1.0.0');
      this.isInitialized = true;

      console.log('OpenTelemetry initialized successfully');
    } catch (error) {
      console.error('Failed to initialize OpenTelemetry:', error);
    }
  }

  /**
   * Create a custom trace span
   * @param name - The name of the span
   * @param fn - The function to execute within the span
   * @returns The result of the function
   */
  async trace<T>(name: string, fn: (span: Span | null) => Promise<T>): Promise<T> {
    if (!this.isInitialized || !this.tracer) {
      console.warn('TelemetryService not initialized, executing without tracing');
      return fn(null);
    }

    return this.tracer.startActiveSpan(name, async (span: Span) => {
      try {
        const result = await fn(span);
        span.setStatus({ code: SpanStatusCode.OK });
        return result;
      } catch (error: any) {
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: error?.message || 'Unknown error',
        });
        span.recordException(error);
        throw error;
      } finally {
        span.end();
      }
    });
  }

  /**
   * Add attributes to the current span
   * @param attributes - Key-value pairs to add to the current span
   */
  addAttributes(attributes: Record<string, string | number | boolean>): void {
    if (!this.isInitialized) {
      return;
    }

    const span = trace.getActiveSpan();
    if (span) {
      Object.entries(attributes).forEach(([key, value]) => {
        span.setAttribute(key, value);
      });
    }
  }

  /**
   * Record an exception in the current span
   * @param error - The error to record
   */
  recordException(error: Error): void {
    if (!this.isInitialized) {
      return;
    }

    const span = trace.getActiveSpan();
    if (span) {
      span.recordException(error);
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: error.message,
      });
    }
  }

  /**
   * Record a queryable application event as a short-lived span.
   * A dedicated span is used because callers may not have an active interaction span.
   */
  trackEvent(name: string, attributes: Record<string, string | number | boolean> = {}): void {
    if (this.isInitialized && this.tracer) {
      const span = this.tracer.startSpan(name, { attributes });
      span.end();
    }

    // The events endpoint binds attributes to string properties, so coerce numbers/booleans to strings
    // for the HTTP payload (the span attributes above keep their original typing).
    const stringAttributes = Object.fromEntries(
      Object.entries(attributes).map(([key, value]) => [key, String(value)])
    );
    this.http?.post<void>(`${getApiUrl(this.runtimeConfig)}/telemetry/events`, {
      name,
      ...stringAttributes
    }).subscribe({
      error: error => console.warn('Failed to record telemetry event:', error)
    });
  }

  /**
   * Shutdown the telemetry provider
   */
  async shutdown(): Promise<void> {
    if (this.provider) {
      await this.provider.shutdown();
      this.isInitialized = false;
    }
  }
}
